
import { useState, FormEvent, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/auth";
import { supabase } from "@/integrations/supabase/client";

interface ShareFormData {
  email: string;
}

/**
 * Custom hook to handle folder sharing functionality
 */
export const useShareFolder = (folderId: string, projectId: string, folderName: string) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Clear error when email changes
  useEffect(() => {
    if (error) setError(null);
  }, [email]);

  /**
   * Handles form submission
   */
  const handleSubmit = async (
    e: FormEvent, 
    onSuccess: (email: string) => void
  ) => {
    e.preventDefault();
    setError(null);

    const formData: ShareFormData = {
      email: email.toLowerCase().trim()
    };

    // Validate email
    if (!formData.email) {
      setError("E-post måste anges");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Ogiltig e-postadress");
      return;
    }

    // Check if user is trying to share with themselves
    if (user?.email && formData.email === user.email.toLowerCase()) {
      setError("Du kan inte dela en mapp med dig själv");
      return;
    }

    try {
      setIsSubmitting(true);

      if (!user) {
        throw new Error("Du måste vara inloggad för att dela en mapp");
      }

      console.log(`Checking if folder ${folderId} is already shared with ${formData.email}`);
      
      // Check if folder is already shared with this email
      const { data: existingShares, error: sharesError } = await supabase
        .from("folder_shares")
        .select()
        .eq("folder_id", folderId)
        .eq("shared_with_email", formData.email);
        
      if (sharesError) throw sharesError;
      
      if (existingShares && existingShares.length > 0) {
        console.log(`Folder ${folderId} is already shared with ${formData.email}`);
        throw new Error(`Mappen är redan delad med ${formData.email}`);
      }

      // Get owner info
      const { data: profileData } = await supabase
        .from("user_profiles")
        .select("first_name, last_name")
        .eq("id", user.id)
        .single();

      // Share the folder
      console.log(`Attempting to share folder ${folderId} with ${formData.email}`);
      const { data: shareResult, error: shareError } = await supabase
        .from("folder_shares")
        .insert({
          folder_id: folderId,
          project_id: projectId,
          owner_id: user.id,
          owner_first_name: profileData?.first_name || "",
          owner_last_name: profileData?.last_name || "",
          shared_with_email: formData.email,
          accepted: false
        })
        .select()
        .single();
        
      if (shareError) throw shareError;
      console.log("Share result:", shareResult);

      // Show success message
      toast.success(`Mappen delades med ${formData.email}`);

      // Call success callback
      onSuccess(formData.email);
      resetForm();
    } catch (error: any) {
      console.error("Error sharing folder:", error);
      setError(error.message || "Ett fel uppstod vid delning av mappen");
      
      // Don't show toast for "already shared" errors as it's handled in the UI
      if (!error.message?.includes("redan delad med")) {
        toast.error(error.message || "Ett fel uppstod vid delning av mappen");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Resets the form state
   */
  const resetForm = () => {
    setEmail("");
    setError(null);
  };

  return {
    email,
    setEmail,
    isSubmitting,
    error,
    handleSubmit,
    resetForm
  };
};
